import { useEffect, useState } from "react";
import { youtubeVideoIdFromRef, type LiveStreamRef } from "@tih/shared";
import { resolveYoutubeStreamPoster } from "../lib/resolve-stream-poster";

type Props = {
  stream: LiveStreamRef;
  /** Event poster URL when YouTube has no still image. */
  posterUrl?: string | null;
  alt?: string;
  className?: string;
  fallbackClassName?: string;
};

/**
 * Stream thumbnail: YouTube CDN still when available, otherwise the event poster.
 */
export function StreamThumbnail({
  stream,
  posterUrl,
  alt = "",
  className,
  fallbackClassName = "live-playlist-thumb-fallback",
}: Props) {
  const videoId = youtubeVideoIdFromRef(stream);
  const sourcePoster = stream.source?.poster ?? null;
  const fallback = posterUrl ?? sourcePoster;
  const [src, setSrc] = useState<string | null>(fallback);

  useEffect(() => {
    if (!videoId) {
      setSrc(fallback);
      return;
    }

    let cancelled = false;
    setSrc(fallback);

    void resolveYoutubeStreamPoster(videoId, fallback).then((url) => {
      if (!cancelled) setSrc(url);
    });

    return () => {
      cancelled = true;
    };
  }, [videoId, fallback]);

  if (!src) {
    return (
      <span className={fallbackClassName} aria-hidden>
        TIH
      </span>
    );
  }

  return <img src={src} alt={alt} loading="lazy" className={className} />;
}
